// Secret-health analysis (M5.1, §20/§25 "secret-health basic"). Runs entirely client-side on
// already-decrypted items — weak + reused password detection. No values are persisted or sent
// anywhere; HIBP and "stale" detection are out of MVP scope (v1.5).
import type { DecryptedItem, Field } from "./items";

// Fields that hold a user-chosen password worth scoring (machine keys/certs/TOTP excluded).
function isPasswordField(f: Field): boolean {
  return f.type === "password" || f.type === "passphrase";
}

const WEAK_BITS = 50; // below this estimated entropy → weak
const WEAK_LEN = 12; // or shorter than this

/** Rough Shannon-style entropy estimate: length × log2(effective charset present). */
export function estimateEntropyBits(value: string): number {
  let charset = 0;
  if (/[a-z]/.test(value)) charset += 26;
  if (/[A-Z]/.test(value)) charset += 26;
  if (/[0-9]/.test(value)) charset += 10;
  if (/[^a-zA-Z0-9]/.test(value)) charset += 32; // symbols (approx)
  if (charset === 0) return 0;
  return value.length * Math.log2(charset);
}

export interface WeakFinding {
  itemId: string;
  itemName: string;
  fieldLabel: string;
  bits: number;
}

export interface ReusedGroup {
  count: number;
  items: { itemId: string; itemName: string; fieldLabel: string }[];
}

export interface HealthReport {
  scannedItems: number;
  passwords: number;
  weak: WeakFinding[];
  reused: ReusedGroup[];
}

/** Analyze decrypted items for weak and reused passwords. Pure; values never leave memory. */
export function analyze(items: DecryptedItem[]): HealthReport {
  const weak: WeakFinding[] = [];
  const byValue = new Map<string, { itemId: string; itemName: string; fieldLabel: string }[]>();
  let passwords = 0;

  for (const it of items) {
    for (const f of it.content.fields) {
      if (!isPasswordField(f) || !f.value) continue;
      passwords++;
      const where = { itemId: it.id, itemName: it.content.name || "(untitled)", fieldLabel: f.label };
      const bits = estimateEntropyBits(f.value);
      if (bits < WEAK_BITS || f.value.length < WEAK_LEN) {
        weak.push({ ...where, bits: Math.round(bits) });
      }
      const group = byValue.get(f.value);
      if (group) group.push(where);
      else byValue.set(f.value, [where]);
    }
  }

  const reused: ReusedGroup[] = [];
  for (const entries of byValue.values()) {
    // Reused only if the same secret appears across ≥2 distinct items.
    const distinctItems = new Set(entries.map((e) => e.itemId));
    if (distinctItems.size >= 2) reused.push({ count: entries.length, items: entries });
  }
  reused.sort((a, b) => b.count - a.count);

  return { scannedItems: items.length, passwords, weak, reused };
}
